import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiCreditCard,
    FiEye,
    FiFileText,
    FiLoader,
    FiPlus,
    FiRefreshCw,
    FiSearch,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
} from "react-router-dom";

import {
    fetchPaymentOutList,
    paymentOutListSelector,
    paymentOutPaginationSelector,
    paymentOutSummarySelector,
    paymentOutLoadingSelector,
    paymentOutErrorSelector,
} from "../../../../../../store/billing/paymentOut/paymentOutSlice";

import MakePaymentDialog from "./components/MakePaymentDialog";
import {
    fetchActiveSuppliers,
    activeSupplierListSelector,
    supplierLoadingSelector,
    supplierErrorSelector,
} from "../../../../../../store/billing/suppliers/supplierSlice";
import "./PaymentOut.css";

const PAGE_SIZE_OPTIONS = [
    10,
    20,
    50,
    100,
];

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
        100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(value)
    );

const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day:
                "2-digit",

            month:
                "short",

            year:
                "numeric",
        }
    ).format(
        date
    );
};

const formatLabel = (
    value
) =>
    String(
        value ||
        "-"
    )
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );

const getSupplierName = (
    payment
) =>
    payment
        ?.supplierSnapshot
        ?.supplierName ||
    payment
        ?.supplierSnapshot
        ?.companyName ||
    payment
        ?.supplier
        ?.supplierName ||
    payment
        ?.supplier
        ?.name ||
    "Supplier";

const getSupplierMobile = (
    payment
) =>
    payment
        ?.supplierSnapshot
        ?.mobile ||
    payment
        ?.supplier
        ?.mobile ||
    "-";

const PaymentOutPage = () => {
    const dispatch =
        useDispatch();

    const history =
        useHistory();

    const payments =
        useSelector(
            paymentOutListSelector
        ) || [];

    const pagination =
        useSelector(
            paymentOutPaginationSelector
        ) || {};

    const summary =
        useSelector(
            paymentOutSummarySelector
        ) || {};

    const loading =
        useSelector(
            paymentOutLoadingSelector
        ) || {};

    const errors =
        useSelector(
            paymentOutErrorSelector
        ) || {};

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        paymentMethod,
        setPaymentMethod,
    ] = useState("");

    const [
        status,
        setStatus,
    ] = useState("");

    const [
        fromDate,
        setFromDate,
    ] = useState("");

    const [
        toDate,
        setToDate,
    ] = useState("");

    const [
        page,
        setPage,
    ] = useState(1);

    const [
        limit,
        setLimit,
    ] = useState(20);

    const [
        paymentDialogOpen,
        setPaymentDialogOpen,
    ] = useState(false);

    const suppliers =
        useSelector(
            activeSupplierListSelector
        );

    const supplierLoading =
        useSelector(
            supplierLoadingSelector
        );

    const supplierErrors =
        useSelector(
            supplierErrorSelector
        );
    /*
    |--------------------------------------------------------------------------
    | LOAD
    |--------------------------------------------------------------------------
    */
    useEffect(
        () => {
            dispatch(
                fetchActiveSuppliers({
                    limit: 500,
                })
            );
        },
        [
            dispatch,
        ]
    );
    const loadPayments =
        useCallback(
            async () => {
                await dispatch(
                    fetchPaymentOutList({
                        page,

                        limit,

                        search:
                            search.trim(),

                        paymentMethod,

                        status,

                        fromDate,

                        toDate,
                    })
                );
            },
            [
                dispatch,
                page,
                limit,
                search,
                paymentMethod,
                status,
                fromDate,
                toDate,
            ]
        );

    useEffect(
        () => {
            const timer =
                setTimeout(
                    () => {
                        loadPayments();
                    },
                    350
                );

            return () =>
                clearTimeout(
                    timer
                );
        },
        [
            loadPayments,
        ]
    );

    /*
    |--------------------------------------------------------------------------
    | SUMMARY
    |--------------------------------------------------------------------------
    */

    const normalizedSummary =
        useMemo(
            () => ({
                totalPayments:
                    toNumber(
                        summary
                            ?.totalPayments
                    ),

                totalAmount:
                    roundMoney(
                        summary
                            ?.totalAmount
                    ),

                allocatedAmount:
                    roundMoney(
                        summary
                            ?.allocatedAmount
                    ),

                unallocatedAmount:
                    roundMoney(
                        summary
                            ?.unallocatedAmount
                    ),
            }),
            [
                summary,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | VIEW
    |--------------------------------------------------------------------------
    */

    const handleView = (
        payment
    ) => {
        if (
            !payment?._id
        ) {
            return;
        }

        history.push(
            `/billing/payment-out/${payment._id}`
        );
    };

    /*
    |--------------------------------------------------------------------------
    | CREATED
    |--------------------------------------------------------------------------
    */

    const handlePaymentCreated =
        async () => {
            setPaymentDialogOpen(
                false
            );

            await loadPayments();
        };

    /*
    |--------------------------------------------------------------------------
    | CLEAR FILTERS
    |--------------------------------------------------------------------------
    */

    const handleClearFilters =
        () => {
            setSearch("");
            setPaymentMethod("");
            setStatus("");
            setFromDate("");
            setToDate("");
            setPage(1);
        };

    /*
    |--------------------------------------------------------------------------
    | PAGINATION
    |--------------------------------------------------------------------------
    */

    const totalRecords =
        toNumber(
            pagination?.total
        );

    const totalPages =
        Math.max(
            toNumber(
                pagination
                    ?.totalPages,
                1
            ),
            1
        );

    const currentPage =
        Math.max(
            toNumber(
                pagination
                    ?.page,
                page
            ),
            1
        );

    const startRecord =
        totalRecords >
            0
            ? (
                currentPage -
                1
            ) *
            limit +
            1
            : 0;

    const endRecord =
        Math.min(
            currentPage *
            limit,
            totalRecords
        );

    return (
        <div className="payment-out-page">
            <header className="payment-out-header">
                <div className="payment-out-heading">
                    <span className="payment-out-heading-icon">
                        <FiCreditCard />
                    </span>

                    <div>
                        <h1>
                            Supplier Payments
                        </h1>

                        <p>
                            Record and manage payments made to suppliers.
                        </p>
                    </div>
                </div>

                <div className="payment-out-header-actions">
                    <button
                        type="button"
                        className="payment-out-button"
                        disabled={
                            loading.list
                        }
                        onClick={
                            loadPayments
                        }
                    >
                        <FiRefreshCw
                            className={
                                loading.list
                                    ? "payment-out-spinner"
                                    : ""
                            }
                        />

                        Refresh
                    </button>

                    <button
                        type="button"
                        className="payment-out-button payment-out-button-primary"
                        onClick={() =>
                            setPaymentDialogOpen(
                                true
                            )
                        }
                    >
                        <FiPlus />

                        Make Payment
                    </button>
                </div>
            </header>

            <section className="payment-out-summary-grid">
                <div className="payment-out-summary-card">
                    <span>
                        Total Payments
                    </span>

                    <strong>
                        {
                            normalizedSummary
                                .totalPayments
                        }
                    </strong>

                    <small>
                        Payment transactions
                    </small>
                </div>

                <div className="payment-out-summary-card payment-out-summary-total">
                    <span>
                        Total Paid
                    </span>

                    <strong>
                        {formatCurrency(
                            normalizedSummary
                                .totalAmount
                        )}
                    </strong>

                    <small>
                        Amount paid to suppliers
                    </small>
                </div>

                <div className="payment-out-summary-card payment-out-summary-allocated">
                    <span>
                        Allocated
                    </span>

                    <strong>
                        {formatCurrency(
                            normalizedSummary
                                .allocatedAmount
                        )}
                    </strong>

                    <small>
                        Applied against purchase bills
                    </small>
                </div>

                <div className="payment-out-summary-card payment-out-summary-advance">
                    <span>
                        Supplier Advance
                    </span>

                    <strong>
                        {formatCurrency(
                            normalizedSummary
                                .unallocatedAmount
                        )}
                    </strong>

                    <small>
                        Unallocated payment
                    </small>
                </div>
            </section>

            <section className="payment-out-toolbar">
                <div className="payment-out-search">
                    <FiSearch />

                    <input
                        type="text"
                        value={
                            search
                        }
                        placeholder="Search payment, supplier, mobile or reference..."
                        onChange={(
                            event
                        ) => {
                            setPage(1);

                            setSearch(
                                event
                                    .target
                                    .value
                            );
                        }}
                    />
                </div>

                <select
                    value={
                        paymentMethod
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(1);

                        setPaymentMethod(
                            event
                                .target
                                .value
                        );
                    }}
                >
                    <option value="">
                        All Methods
                    </option>

                    <option value="CASH">
                        Cash
                    </option>

                    <option value="UPI">
                        UPI
                    </option>

                    <option value="BANK_TRANSFER">
                        Bank Transfer
                    </option>

                    <option value="CHEQUE">
                        Cheque
                    </option>

                    <option value="CARD">
                        Card
                    </option>

                    <option value="OTHER">
                        Other
                    </option>
                </select>

                <select
                    value={
                        status
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(1);

                        setStatus(
                            event
                                .target
                                .value
                        );
                    }}
                >
                    <option value="">
                        All Status
                    </option>

                    <option value="COMPLETED">
                        Completed
                    </option>

                    <option value="DRAFT">
                        Draft
                    </option>

                    <option value="CANCELLED">
                        Cancelled
                    </option>
                </select>

                <input
                    type="date"
                    value={
                        fromDate
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(1);

                        setFromDate(
                            event
                                .target
                                .value
                        );
                    }}
                />

                <input
                    type="date"
                    value={
                        toDate
                    }
                    min={
                        fromDate ||
                        undefined
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(1);

                        setToDate(
                            event
                                .target
                                .value
                        );
                    }}
                />

                <button
                    type="button"
                    className="payment-out-button"
                    onClick={
                        handleClearFilters
                    }
                >
                    Clear
                </button>
            </section>

            {errors.list && (
                <div className="payment-out-alert">
                    {
                        errors.list
                    }
                </div>
            )}

            <section className="payment-out-card">
                {loading.list &&
                    payments.length ===
                    0 ? (
                    <div className="payment-out-empty">
                        <FiLoader className="payment-out-spinner" />

                        <h3>
                            Loading supplier payments...
                        </h3>
                    </div>
                ) : payments.length ===
                    0 ? (
                    <div className="payment-out-empty">
                        <FiFileText />

                        <h3>
                            No supplier payments found
                        </h3>

                        <p>
                            Click Make Payment to record your first supplier payment.
                        </p>
                    </div>
                ) : (
                    <div className="payment-out-table-wrapper">
                        <table className="payment-out-table">
                            <thead>
                                <tr>
                                    <th>
                                        Payment
                                    </th>

                                    <th>
                                        Supplier
                                    </th>

                                    <th>
                                        Date
                                    </th>

                                    <th>
                                        Method
                                    </th>

                                    <th>
                                        Reference
                                    </th>

                                    <th className="text-right">
                                        Amount
                                    </th>

                                    <th className="text-right">
                                        Allocated
                                    </th>

                                    <th className="text-right">
                                        Advance
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th className="text-right">
                                        Action
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {payments.map(
                                    (
                                        payment,
                                        index
                                    ) => (
                                        <tr
                                            key={
                                                payment
                                                    ?._id ||
                                                index
                                            }
                                        >
                                            <td>
                                                <button
                                                    type="button"
                                                    className="payment-out-link"
                                                    onClick={() =>
                                                        handleView(
                                                            payment
                                                        )
                                                    }
                                                >
                                                    {payment
                                                        ?.paymentNumber ||
                                                        "-"}
                                                </button>
                                            </td>

                                            <td>
                                                <div className="payment-out-supplier">
                                                    <strong>
                                                        {getSupplierName(
                                                            payment
                                                        )}
                                                    </strong>

                                                    <span>
                                                        {getSupplierMobile(
                                                            payment
                                                        )}
                                                    </span>
                                                </div>
                                            </td>

                                            <td>
                                                {formatDate(
                                                    payment
                                                        ?.paymentDate
                                                )}
                                            </td>

                                            <td>
                                                <span className="payment-out-method">
                                                    {formatLabel(
                                                        payment
                                                            ?.paymentMethod
                                                    )}
                                                </span>
                                            </td>

                                            <td>
                                                {payment
                                                    ?.referenceNumber ||
                                                    "-"}
                                            </td>

                                            <td className="text-right payment-out-amount">
                                                {formatCurrency(
                                                    payment
                                                        ?.amount
                                                )}
                                            </td>

                                            <td className="text-right">
                                                {formatCurrency(
                                                    payment
                                                        ?.allocatedAmount
                                                )}
                                            </td>

                                            <td className="text-right">
                                                {formatCurrency(
                                                    payment
                                                        ?.unallocatedAmount
                                                )}
                                            </td>

                                            <td>
                                                <span
                                                    className={`payment-out-status payment-out-status-${String(
                                                        payment
                                                            ?.status ||
                                                        "COMPLETED"
                                                    ).toLowerCase()}`}
                                                >
                                                    {formatLabel(
                                                        payment
                                                            ?.status ||
                                                        "COMPLETED"
                                                    )}
                                                </span>
                                            </td>

                                            <td className="text-right">
                                                <button
                                                    type="button"
                                                    className="payment-out-view-button"
                                                    onClick={() =>
                                                        handleView(
                                                            payment
                                                        )
                                                    }
                                                >
                                                    <FiEye />

                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <footer className="payment-out-pagination">
                    <div>
                        <span>
                            Showing{" "}
                            <strong>
                                {
                                    startRecord
                                }
                            </strong>
                            {" - "}
                            <strong>
                                {
                                    endRecord
                                }
                            </strong>
                            {" of "}
                            <strong>
                                {
                                    totalRecords
                                }
                            </strong>
                        </span>

                        <select
                            value={
                                limit
                            }
                            onChange={(
                                event
                            ) => {
                                setPage(1);

                                setLimit(
                                    Number(
                                        event
                                            .target
                                            .value
                                    )
                                );
                            }}
                        >
                            {PAGE_SIZE_OPTIONS.map(
                                (
                                    size
                                ) => (
                                    <option
                                        key={
                                            size
                                        }
                                        value={
                                            size
                                        }
                                    >
                                        {size}
                                    </option>
                                )
                            )}
                        </select>
                    </div>

                    <div>
                        <button
                            type="button"
                            disabled={
                                currentPage <=
                                1 ||
                                loading.list
                            }
                            onClick={() =>
                                setPage(
                                    Math.max(
                                        currentPage -
                                        1,
                                        1
                                    )
                                )
                            }
                        >
                            Previous
                        </button>

                        <span>
                            Page{" "}
                            <strong>
                                {
                                    currentPage
                                }
                            </strong>{" "}
                            of{" "}
                            <strong>
                                {
                                    totalPages
                                }
                            </strong>
                        </span>

                        <button
                            type="button"
                            disabled={
                                currentPage >=
                                totalPages ||
                                loading.list
                            }
                            onClick={() =>
                                setPage(
                                    Math.min(
                                        currentPage +
                                        1,
                                        totalPages
                                    )
                                )
                            }
                        >
                            Next
                        </button>
                    </div>
                </footer>
            </section>

            <MakePaymentDialog
                open={
                    paymentDialogOpen
                }
                onClose={() =>
                    setPaymentDialogOpen(
                        false
                    )
                }
                suppliers={
                    suppliers
                }
                supplierLoading={
                    Boolean(
                        supplierLoading
                            ?.active
                    )
                }
                supplierError={
                    supplierErrors
                        ?.active ||
                    ""
                }
                onSuccess={
                    handlePaymentCreated
                }
            />
        </div>
    );
};

export default PaymentOutPage;